import React, { useCallback, useEffect, useState } from 'react';
import { listDashboards, saveDashboard, deleteDashboard, runInsight, getInsightCatalog } from '../api/modulesApi';
import { errorMessage } from '../api/securityApi';
import ModuleNav from '../components/Modules/ModuleNav';
import SiteSelector, { useActiveSite } from '../components/Security/SiteSelector';
import InsightView from '../components/Modules/InsightView';
import RequirePermission from '../auth/RequirePermission';

const parse = (d) => { try { return JSON.parse(d.widgetsJson || '[]'); } catch { return []; } };

function Widget({ w, siteId, onRemove, editing }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    runInsight(w.insight, { siteId, days: w.days || 7 }).then((d) => alive && setData(d)).catch((e) => alive && setErr(errorMessage(e)));
    return () => { alive = false; };
  }, [w.insight, w.days, siteId]);
  return (
    <div className="sec-card" style={{ gridColumn: w.wide ? '1/-1' : 'auto', minWidth: 0 }}>
      <div className="sec-row" style={{ justifyContent: 'space-between' }}>
        <h3 style={{ margin: 0 }}>{w.title || data?.title || w.insight}</h3>
        {editing && <button className="ghost danger" onClick={onRemove}>Remove</button>}
      </div>
      {err ? <div className="muted">{err}</div> : data ? <InsightView result={data.result} compact /> : <div className="muted">Loading…</div>}
    </div>
  );
}

export default function MyDashboardsPage() {
  const { sites, siteId, setSiteId } = useActiveSite();
  const [list, setList] = useState([]);
  const [current, setCurrent] = useState(null); // {id?, name, isDefault, widgets[]}
  const [editing, setEditing] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [pick, setPick] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const load = useCallback(async () => {
    try {
      const [l, c] = await Promise.all([listDashboards(), getInsightCatalog()]);
      setList(l); setCatalog(c);
      setCurrent((cur) => cur || (l.length ? { id: l.find((x) => x.isDefault)?.id || l[0].id, name: (l.find((x) => x.isDefault) || l[0]).name, isDefault: (l.find((x) => x.isDefault) || l[0]).isDefault, widgets: parse(l.find((x) => x.isDefault) || l[0]) } : null));
    } catch (e) { setError(errorMessage(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const select = (d) => { setEditing(false); setCurrent({ id: d.id, name: d.name, isDefault: d.isDefault, widgets: parse(d) }); };
  const save = async () => {
    setError(''); setInfo('');
    try {
      const r = await saveDashboard(current.id, { name: current.name, isDefault: current.isDefault, widgetsJson: JSON.stringify(current.widgets) });
      setCurrent({ ...current, id: r.id }); setEditing(false); setInfo('Dashboard saved.'); await load();
    } catch (e) { setError(errorMessage(e)); }
  };

  return (
    <div className="shell">
      <header className="page-header"><div><h1>My dashboards</h1>
        <p className="muted">Build your own screen from any report. Widgets refresh when you open the page.</p></div></header>
      <ModuleNav />
      {error && <div className="toast error">{error}</div>}
      {info && <div className="toast info">{info}</div>}
      <div className="sec-toolbar">
        <SiteSelector sites={sites} value={siteId} onChange={setSiteId} />
        {list.map((d) => <button key={d.id} className={current?.id === d.id ? 'primary' : 'ghost'} onClick={() => select(d)}>{d.name}{d.isDefault ? ' ★' : ''}</button>)}
        <RequirePermission code="workspace.write">
          <button className="ghost" onClick={() => { setCurrent({ name: 'New dashboard', isDefault: list.length === 0, widgets: [] }); setEditing(true); }}>+ New dashboard</button>
        </RequirePermission>
      </div>

      {!current && <p className="muted">You have no dashboards yet. Create one and add widgets.</p>}
      {current && (
        <>
          <RequirePermission code="workspace.write">
            <div className="sec-card" style={{ marginBottom: 16 }}>
              {!editing ? (
                <div className="sec-row">
                  <button className="primary" onClick={() => setEditing(true)}>Edit layout</button>
                  {current.id && <button className="ghost danger" onClick={async () => { if (window.confirm('Delete this dashboard?')) { await deleteDashboard(current.id); setCurrent(null); await load(); } }}>Delete</button>}
                </div>
              ) : (
                <div className="mod-form" style={{ maxWidth: 'none' }}>
                  <div className="sec-row">
                    <input className="sec-input" value={current.name} onChange={(e) => setCurrent({ ...current, name: e.target.value })} />
                    <label><input type="checkbox" checked={current.isDefault} onChange={(e) => setCurrent({ ...current, isDefault: e.target.checked })} /> Default</label>
                  </div>
                  <div className="sec-row">
                    <select className="sec-select" style={{ minWidth: 280 }} value={pick} onChange={(e) => setPick(e.target.value)}>
                      <option value="">Add a widget…</option>
                      {catalog.map((c) => <option key={c.key} value={c.key}>{c.group} — {c.title}</option>)}
                    </select>
                    <button className="ghost" disabled={!pick} onClick={() => { setCurrent({ ...current, widgets: [...current.widgets, { insight: pick, days: 7, wide: true }] }); setPick(''); }}>Add</button>
                  </div>
                  {current.widgets.map((w, i) => (
                    <div key={i} className="sec-row">
                      <span style={{ flex: 1 }}>{catalog.find((c) => c.key === w.insight)?.title || w.insight}</span>
                      <select className="sec-select" style={{ minWidth: 100 }} value={w.days} onChange={(e) => setCurrent({ ...current, widgets: current.widgets.map((x, j) => j === i ? { ...x, days: Number(e.target.value) } : x) })}>
                        {[1, 7, 14, 30, 90].map((d) => <option key={d} value={d}>{d} days</option>)}</select>
                      <label><input type="checkbox" checked={!!w.wide} onChange={(e) => setCurrent({ ...current, widgets: current.widgets.map((x, j) => j === i ? { ...x, wide: e.target.checked } : x) })} /> Full width</label>
                      <button className="ghost" disabled={i === 0} onClick={() => { const a = [...current.widgets]; [a[i - 1], a[i]] = [a[i], a[i - 1]]; setCurrent({ ...current, widgets: a }); }}>↑</button>
                      <button className="ghost danger" onClick={() => setCurrent({ ...current, widgets: current.widgets.filter((_, j) => j !== i) })}>Remove</button>
                    </div>
                  ))}
                  <div className="sec-row"><button className="primary" onClick={save}>Save dashboard</button><button className="ghost" onClick={() => { setEditing(false); load(); }}>Cancel</button></div>
                </div>
              )}
            </div>
          </RequirePermission>
          <div className="mod-two">
            {current.widgets.map((w, i) => <Widget key={`${w.insight}-${i}-${siteId}-${w.days}`} w={w} siteId={siteId} editing={false} />)}
          </div>
          {current.widgets.length === 0 && <p className="muted">This dashboard is empty. Use “Edit layout” to add widgets.</p>}
        </>
      )}
    </div>
  );
}
